/**
 * Coach-intelligence Rung 2 §2.1 — the reaction TAP: the only code path that
 * mints an activation nonce.
 *
 * Wraps a Rung-1 reaction href (`/v2/chat?coach=training-coach&run=<id>`).
 * Flag ON + a run-carrying coach href ⇒ returns a click handler that writes
 * the nonce into the NEW history entry (react-router `navigate` state →
 * history.state.usr) and performs the navigation. Flag OFF, or any other
 * href ⇒ returns undefined and the anchor behaves exactly as today
 * (byte-identical, §15).
 */

import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useServerFlag } from './useServerFlag';
import {
  coachChatRouterPath,
  mintActivation,
  parseRunIdFromCoachHref,
} from '../lib/run/runContextActivation';

export type ReactionTapHandler = (e: { preventDefault: () => void }) => void;

export function useReactionTapActivation(href: string | null | undefined): ReactionTapHandler | undefined {
  const flagOn = useServerFlag('coach_convo_v1');
  const navigate = useNavigate();
  const runId = flagOn ? parseRunIdFromCoachHref(href) : null;

  const handler = useCallback<ReactionTapHandler>(
    (e) => {
      // Recompute inside the handler so a stale closure can never mint for
      // the wrong run (href is also a dep — this is belt-and-suspenders).
      const id = parseRunIdFromCoachHref(href);
      // navigate() takes the BASENAME-relative route (`/chat?…`), never the
      // absolute `/v2/chat?…` (web basename is /v2 — it would double-prefix).
      const to = coachChatRouterPath(href);
      if (!id || !to) return;
      e.preventDefault();
      navigate(to, { state: { runContextActivation: mintActivation(id) } });
    },
    [href, navigate],
  );

  return flagOn && runId ? handler : undefined;
}
